import React, { memo } from 'react'
import { Controller } from 'react-hook-form'

import { useHandler } from '@surglogs-app/hooks'
import type { RemindersEditorReminderAPIEntity } from '@surglogs-app/network'
import { FlexBox, RemoveButton } from '@surglogs-app/ui'

import { useEventCapture } from '../../../analytics/src'
import { REMINDERS_EDITOR_OPTION_CUSTOM } from '../constants'
import { parseReminderOptionText } from '../services/RemindersService'
import type {
  RemindersEditorAnalyticsContext,
  RemindersEditorFormData,
  RemindersEditorReminderValue,
} from '../types'

import { RemindersEditorListItemAddDefaultCheckbox } from './RemindersEditorListItemAddDefaultCheckbox'
import { RemindersEditorListItemAmountInput } from './RemindersEditorListItemAmountInput'
import { RemindersEditorListItemSelect } from './RemindersEditorListItemSelect'
import { RemindersEditorListItemUnitSelect } from './RemindersEditorListItemUnitSelect'

interface RemindersEditorListItemProps {
  disabled?: boolean
  disableFirstRemove?: boolean
  index: number
  hasSaveRemindersPermissions: boolean
  update: (index: number, value: RemindersEditorReminderValue) => void
  reminder: RemindersEditorReminderValue
  clinicReminders?: RemindersEditorReminderAPIEntity[]
  selectedReminders: RemindersEditorReminderValue[]
  allowCreateReminder?: boolean
  onRemove: (idx: number) => void
  context: RemindersEditorAnalyticsContext
  control: any
  selectedOption: string | undefined
  setSelectedOption: (k: number, v: string | undefined) => void
}

export const RemindersEditorListItem = memo(
  ({
    disableFirstRemove,
    disabled,
    index,
    hasSaveRemindersPermissions,
    update,
    clinicReminders,
    selectedReminders,
    allowCreateReminder,
    onRemove,
    context,
    control,
    selectedOption,
    setSelectedOption,
  }: RemindersEditorListItemProps) => {
    const { captureEvent } = useEventCapture()

    const onChange = useHandler(
      (val: string, props: RemindersEditorReminderValue) => {
        setSelectedOption(index, val)
        captureEvent('reminder_selected', {
          context,
          value: val,
        })

        if (val !== REMINDERS_EDITOR_OPTION_CUSTOM) {
          update(index, {
            ...parseReminderOptionText(val),
            default: props.default,
          })
        } else {
          update(index, {
            amount: null,
            unit: null,
            default: false,
          })
        }
      },
    )

    return (
      <FlexBox
        {...{
          fd: 'column',
          w: '100%',
          gap: 'sm',
          role: 'listitem',
          'aria-label': 'reminder-item',
        }}>
        <FlexBox {...{ w: '100%', gap: 'sm' }}>
          <RemindersEditorListItemSelect
            {...{
              disabled,
              value: selectedOption,
              selectedReminders,
              onChange,
              clinicReminders,
              allowCreateReminder,
              'aria-label': 'reminder-select',
              menuInPortal: true,
              invalid: !selectedOption,
            }}
          />
          {!disabled && (disableFirstRemove ? index !== 0 : true) ? (
            <RemoveButton
              {...{
                onClick: () => onRemove(index),
                disabled,
                'aria-label': 'remove-reminder',
                shape: 'square',
              }}
            />
          ) : null}
        </FlexBox>

        {selectedOption === REMINDERS_EDITOR_OPTION_CUSTOM ? (
          <FlexBox {...{ flex: 1, fd: 'column' }}>
            <FlexBox {...{ flex: '1', ai: 'center', gap: 'sm' }}>
              <Controller<
                RemindersEditorFormData,
                `reminders.${typeof index}.amount`
              >
                {...{
                  control,
                  name: `reminders.${index}.amount`,
                  render: ({
                    field: { onChange: onAmountChange, value },
                    formState,
                  }) => (
                    <RemindersEditorListItemAmountInput
                      {...{
                        disabled,
                        onChange: onAmountChange,
                        value: value ?? '',
                        invalid: !!formState.errors.reminders?.[index]?.amount,
                      }}
                    />
                  ),
                }}
              />

              <Controller<
                RemindersEditorFormData,
                `reminders.${typeof index}.unit`
              >
                {...{
                  control,
                  name: `reminders.${index}.unit`,
                  render: ({
                    field: { onChange: onUnitChange, value },
                    formState,
                  }) => (
                    <RemindersEditorListItemUnitSelect
                      {...{
                        disabled,
                        onChange: onUnitChange,
                        value: value ?? undefined,
                        menuInPortal: true,
                        invalid: !!formState.errors.reminders?.[index]?.unit,
                        'aria-label': 'reminder-unit-input',
                      }}
                    />
                  ),
                }}
              />
            </FlexBox>
            {hasSaveRemindersPermissions ? (
              <FlexBox {...{ flex: '1', ai: 'center' }}>
                <Controller<
                  RemindersEditorFormData,
                  `reminders.${typeof index}.default`
                >
                  {...{
                    control,
                    name: `reminders.${index}.default`,
                    render: ({
                      field: { onChange: onCheckboxChange, value },
                    }) => (
                      <RemindersEditorListItemAddDefaultCheckbox
                        {...{
                          onChange: onCheckboxChange,
                          checked: !!value,
                          disabled,
                        }}
                      />
                    ),
                  }}
                />
              </FlexBox>
            ) : null}
          </FlexBox>
        ) : null}
      </FlexBox>
    )
  },
)
